import React, { useEffect, useRef, useState } from 'react';
import { Avatar, Box, ButtonBase, CircularProgress, Divider, IconButton, ListItemIcon, Menu, MenuItem, Tooltip, Typography } from '@mui/material';
import {
  Battery5BarOutlined,
  ElectricMeterOutlined,
  HubOutlined,
  LocationOnOutlined,
  PlaceOutlined,
  PowerOutlined,
  ReceiptLongOutlined,
  SolarPowerOutlined,
  SpaceDashboardOutlined,
  SyncAlt,
  TerminalOutlined,
  DarkModeOutlined,
  KeyboardArrowDown,
  LightModeOutlined,
  Logout,
} from '@mui/icons-material';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import DashboardSidebar from './DashboardSidebar';
import { useDashboardContext } from '../../components/providers/DashboardProvider';
import { useIdleTimer } from 'react-idle-timer';
import { useAuth } from '../../components/providers/AuthProvider';
import { useColorMode } from '../../components/providers/ColorModeProvider';

const navItems = [
  {
    title: "Locations",
    section: "Monitor",
    route: "/dashboard/locations",
    // icon: <img src={app_icon_design} />,
    icon: <LocationOnOutlined />
  },
  {
    title: "Overview",
    section: "Monitor",
    route: "/dashboard/overview",
    // icon: <img src={app_icon_design} />,
    icon: <SpaceDashboardOutlined />
  },
  {
    title: "Utility Bill",
    section: "Monitor",
    route: "/dashboard/utility_bill",
    // icon: <img src={app_icon_design} />,
    icon: <ReceiptLongOutlined />
  },
  {
    title: "Grid",
    section: "Systems",
    route: "/dashboard/grid",
    // icon: <img src={app_icon_ai}/>,
    icon: <ElectricMeterOutlined />,
    allowedRoles: ["admin", "advanced_viewer"],
  },
  {
    title: "Load",
    section: "Systems",
    route: "/dashboard/load",
    // icon: <img src={app_icon_ai}/>,
    icon: <PowerOutlined />,
    allowedRoles: ["admin", "advanced_viewer"],
  },
  {
    title: "Solar",
    section: "Systems",
    route: "/dashboard/solar",
    // icon: <img src={app_icon_ai}/>,
    icon: <SolarPowerOutlined />,
    allowedRoles: ["admin", "advanced_viewer"],
  },
  {
    title: "Batteries",
    section: "Systems",
    route: "/dashboard/battery",
    // icon: <img src={app_icon_ai}/>,
    icon: <Battery5BarOutlined />,
    allowedRoles: ["admin", "advanced_viewer"],
  },
  {
    title: "AC / DC",
    section: "Systems",
    route: "/dashboard/pcs",
    // icon: <img src={app_icon_ai}/>,
    icon: <SyncAlt />,
    allowedRoles: ["admin", "advanced_viewer"],
  },
  {
    title: "Devices",
    section: "Admin",
    route: "/dashboard/devices",
    // icon: <img src={app_icon_ai}/>,
    icon: <HubOutlined />,
    allowedRoles: ["admin"],
  },
  // {
  //   title: "Control",
  //   route: "/dashboard/control",
  //   // icon: <img src={app_icon_ai}/>,
  //   icon: <TuneOutlined />
  // },
  {
    title: "Terminal",
    section: "Admin",
    route: "/dashboard/terminal",
    // icon: <img src={app_icon_ai}/>,
    icon: <TerminalOutlined />,
    allowedRoles: ["admin"],
  },
]

const Dashboard = () => {
  const drawerWidth = 255;

  const navigate = useNavigate();
  const currentPage = useLocation();

  const { user, logout } = useAuth();
  const { mode: colorMode, toggleMode: toggleColorMode } = useColorMode();

  const userType = user?.profile?.user_type;
  const visibleNavItems = navItems.filter(
    (item) => !item.allowedRoles || item.allowedRoles.includes(userType)
  );

  const { selectedCluster, autoSelectCluster } = useDashboardContext();
  const { pollingCounter, setPollingCounter } = useDashboardContext();
  const { loading } = useDashboardContext();

  const [activeTabTitle, setActiveTabTitle] = useState(navItems[0].text);
  const [selectedTab, setSelectedTab] = useState(navItems[0]);

  const [showLoading, setShowLoading] = useState(false);
  const [userMenuAnchor, setUserMenuAnchor] = useState(null);

  const idleTimeout = 30000; //ms
  const pollingInterval = 5000; //ms

  // const [pollCounter, setPollCounter] = useState(0);
  const [polling, setPolling] = useState(false);
  const [isIdle, setIsIdle] = useState(false);
  // The polling interval is created once, so it reads idleness from a ref instead of the stale state value
  const isIdleRef = useRef(false);

  const handleIdle = () => {
    console.log("Idle");
    isIdleRef.current = true;
    setIsIdle(true);
  }

  const handleActive = () => {
    console.log("Active");
    isIdleRef.current = false;
    setIsIdle(false);
    setPollingCounter((prev) => prev + 1);
  }

  const pollingData = () => {
    setPollingCounter((prev) => prev + 1);
  }

  useIdleTimer({
    timeout: idleTimeout,
    onIdle: handleIdle,
    onActive: handleActive,
    debounce: 500, // Debounce delay for activity detection
  });

  const smartSelectCluster = async () => {
    // If current not selected any cluster, set a default cluster
    // Or get ID from cookies
    if (selectedCluster == null) {
      console.log(selectedCluster);
      await autoSelectCluster();
      console.log("Selected Null. Go to locations");
      setSelectedTab(navItems[0]);
    }
  }

  function goToDefaultPage() {
      navigate("/dashboard/locations");
  }

  useEffect(() => {
    let interval;

    // Only when user authenticated
    if (user) {

      if (polling) {
        interval = setInterval(() => {
          // fetchOpenedProjectInfo();

          // smartSelectCluster();

          if (!isIdleRef.current) {
            pollingData();
          }
        }, pollingInterval);
      }
    }

    return () => clearInterval(interval);
  }, [polling]);

  useEffect(() => {
    setPolling(true);
  }, []);

  useEffect(() => {
    if (selectedCluster == null) {
      goToDefaultPage();
    } else {
      // console.log(selectedCluster);
    }
  }, [selectedCluster]);

  const handleMenuItemClick = async (target) => {
    setSelectedTab(target);
  }

  useEffect(() => {
    navigate(selectedTab.route);
  }, [selectedTab]);

  useEffect(() => {
    // console.log(currentPage);
    const currentRoute = currentPage.pathname;
    visibleNavItems.forEach((item) => {
      if (item.route == currentRoute) {
        setActiveTabTitle(item.title);
        setSelectedTab(item);
      }
    });
  }, [currentPage])

  useEffect(() => {
    if (!selectedCluster) {
      goToDefaultPage();
    }
  }, [pollingCounter]);

  const profileName = user?.profile?.profile_name ?? "";
  const profileTitle = user?.profile?.profile_title ?? "";
  const initials = profileName
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map(part => part[0].toUpperCase())
    .join("");

  const onLocationsPage = activeTabTitle === visibleNavItems[0].title;

  return (
    <Box sx={{
      display: 'flex',
      minHeight: '100%',
      position: 'relative',
      flexDirection: 'column',
    }}
    >
      <Box sx={{
        display: 'flex',
        height: '100%',
      }}
      >

        <DashboardSidebar
          width={drawerWidth}
          navItems={visibleNavItems}
          activeTab={selectedTab}
          onItemClick={handleMenuItemClick}
        />

        <Box sx={{
          width: '100%',
          minWidth: 0,
          ml: `${drawerWidth}px`,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          backgroundColor: "var(--c-bg)",
        }}
        >

          {/* Page header */}
          <Box
            component="header"
            sx={{
              position: "sticky",
              top: 0,
              zIndex: 10,
              width: "100%",
              height: 64,
              display: "flex",
              justifyContent: "center",
              backgroundColor: "var(--c-header-bg)",
              backdropFilter: "blur(8px)",
              borderBottom: "1px solid var(--c-border)",
            }}
          >
            <Box sx={{
              display: "flex",
              alignItems: "center",
              gap: 2,
              width: "100%",
              maxWidth: "1440px",
              px: 3,
              boxSizing: "border-box",
            }}>
              {/* Left: page title and current site */}
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flex: 1, minWidth: 0 }}>
                <Typography
                  component="h1"
                  sx={{
                    color: "var(--c-text)",
                    fontWeight: 700,
                    fontSize: 20,
                    letterSpacing: "-0.01em",
                    whiteSpace: "nowrap",
                  }}
                >
                  {activeTabTitle}
                </Typography>

                {!onLocationsPage && selectedCluster && (
                  <ButtonBase
                    onClick={() => navigate("/dashboard/locations")}
                    title="Change location"
                    sx={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 0.5,
                      minWidth: 0,
                      px: 1,
                      py: 0.5,
                      borderRadius: "8px",
                      border: "1px solid var(--c-border)",
                      backgroundColor: "var(--c-surface)",
                      color: "var(--c-text-3)",
                      transition: "background-color 120ms ease, border-color 120ms ease",
                      "&:hover": { backgroundColor: "var(--c-subtle)", borderColor: "var(--c-border-strong)" },
                    }}
                  >
                    <PlaceOutlined sx={{ fontSize: 16, color: "var(--c-accent)" }} />
                    <Typography sx={{ fontSize: 13, fontWeight: 500 }} noWrap>
                      {selectedCluster.cluster_name}
                    </Typography>
                    <KeyboardArrowDown sx={{ fontSize: 16, color: "var(--c-text-faint)" }} />
                  </ButtonBase>
                )}
              </Box>

              {/* Right: data status and user */}
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                {!onLocationsPage && (
                  <Box
                    title={isIdle ? "Updates paused while you're inactive" : "Data refreshes every few seconds"}
                    sx={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 0.75,
                      px: 1.25,
                      py: 0.5,
                      borderRadius: "999px",
                      backgroundColor: isIdle ? "var(--c-muted)" : "var(--c-success-bg)",
                      color: isIdle ? "var(--c-text-muted)" : "var(--c-success)",
                      fontSize: 12,
                      fontWeight: 600,
                      whiteSpace: "nowrap",
                    }}
                  >
                    {loading
                      ? <CircularProgress size={10} thickness={6} sx={{ color: "inherit" }} />
                      : <Box component="span" sx={{
                        width: 7,
                        height: 7,
                        borderRadius: "50%",
                        backgroundColor: isIdle ? "var(--c-text-faint)" : "#22C55E",
                      }} />
                    }
                    {isIdle ? "Paused" : loading ? "Updating" : "Live"}
                  </Box>
                )}

                <Tooltip title={colorMode === "dark" ? "Switch to light mode" : "Switch to dark mode"}>
                  <IconButton
                    onClick={toggleColorMode}
                    aria-label={colorMode === "dark" ? "Switch to light mode" : "Switch to dark mode"}
                    sx={{
                      width: 36,
                      height: 36,
                      border: "1px solid var(--c-border)",
                      borderRadius: "10px",
                      color: "var(--c-text-3)",
                      "&:hover": { backgroundColor: "var(--c-muted)", color: "var(--c-text)" },
                    }}
                  >
                    {colorMode === "dark"
                      ? <LightModeOutlined sx={{ fontSize: 18 }} />
                      : <DarkModeOutlined sx={{ fontSize: 18 }} />}
                  </IconButton>
                </Tooltip>

                <ButtonBase
                  onClick={(e) => setUserMenuAnchor(e.currentTarget)}
                  aria-label="Account menu"
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.25,
                    pl: 0.5,
                    pr: 1,
                    py: 0.5,
                    borderRadius: "999px",
                    transition: "background-color 120ms ease",
                    "&:hover": { backgroundColor: "var(--c-muted)" },
                  }}
                >
                  <Avatar sx={{
                    width: 32,
                    height: 32,
                    fontSize: 13,
                    fontWeight: 600,
                    backgroundColor: "var(--c-accent-bg-strong)",
                    color: "var(--c-accent)",
                  }}>
                    {initials || null}
                  </Avatar>
                  <Box sx={{ textAlign: "left", display: { xs: "none", md: "block" } }}>
                    <Typography sx={{ fontSize: 13, fontWeight: 600, lineHeight: 1.3, color: "var(--c-text)" }}>
                      {profileName}
                    </Typography>
                    <Typography sx={{ fontSize: 12, lineHeight: 1.3, color: "var(--c-text-muted)" }}>
                      {profileTitle}
                    </Typography>
                  </Box>
                  <KeyboardArrowDown sx={{ fontSize: 18, color: "var(--c-text-faint)" }} />
                </ButtonBase>
                <Menu
                  anchorEl={userMenuAnchor}
                  open={Boolean(userMenuAnchor)}
                  onClose={() => setUserMenuAnchor(null)}
                  anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                  transformOrigin={{ vertical: "top", horizontal: "right" }}
                  slotProps={{
                    paper: {
                      sx: {
                        mt: 1,
                        minWidth: 200,
                        borderRadius: "12px",
                        border: "1px solid var(--c-border)",
                        boxShadow: "0 8px 24px rgba(15, 23, 42, 0.08)",
                      },
                    },
                  }}
                >
                  <Box sx={{ px: 2, py: 1 }}>
                    <Typography sx={{ fontSize: 13, fontWeight: 600, color: "var(--c-text)" }}>{profileName}</Typography>
                    <Typography sx={{ fontSize: 12, color: "var(--c-text-muted)" }}>{profileTitle}</Typography>
                  </Box>
                  <Divider sx={{ my: 0.5 }} />
                  <MenuItem
                    onClick={() => { setUserMenuAnchor(null); logout(); }}
                    sx={{ fontSize: 14, color: "var(--c-danger)", mx: 0.5, borderRadius: "8px" }}
                  >
                    <ListItemIcon sx={{ color: "inherit" }}><Logout fontSize="small" /></ListItemIcon>
                    Log out
                  </MenuItem>
                </Menu>
              </Box>
            </Box>
          </Box>

          {/* Content */}
          <Box sx={{
            display: "flex",
            flexDirection: "column",
            boxSizing: "border-box",
            width: "100%",
            maxWidth: "1440px",
            px: 3,
            pt: 1,
            pb: 4,
          }}
          >
            <Outlet onPolling={null} goToDefaultPage={goToDefaultPage} />
          </Box>

        </Box>
      </Box>
    </Box>
  );
};

export default Dashboard;
